import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import type { ProcedureCategory, ProcedureCode } from "@dentacore/shared-types";

import { createProcedureCode } from "../api/catalogue";
import { listProcedureCategories } from "../api/procedure-categories";
import { describeNumberError, NumberField } from "../ui/number-field";
import type { NumberFieldRules } from "../ui/number-field";
import styles from "./quick-procedure.module.css";
import { RequiredMark } from "../ui/required-mark";
import { useBookingScoped } from "./booking-location";

/**
 * Bounds, not defaults.
 *
 * Five minutes is the shortest slot the day grid can draw and eight hours is
 * the longest a chair can hold in one booking — both are properties of the
 * application, not opinions about dentistry. What a given procedure takes is
 * the practice's to say.
 */
const MINUTES: NumberFieldRules = { min: 5, max: 480, integer: true, unit: "minutes" };

/** A price has a fractional part in most currencies, so no `integer` here. */
const PRICE: NumberFieldRules = { min: 0 };

/**
 * Adding a procedure to this location's catalogue without leaving the visit.
 *
 * ── Why it is here and not only in Settings ─────────────────────────────────
 *
 * The catalogue is per branch and ships empty (ADR-0008), and a clinician
 * finishing a visit is the person who knows what was actually done. Told the
 * work is not in the list, their options were to abandon the dialog, walk to
 * Settings, add it, come back and start again — or to complete without charting
 * and lose the tooth, the history and the invoice. The client asked for the
 * third way: *"in case the procedure is not there i should be able to create a
 * procedure and then add it."*
 *
 * ── Deliberately the short form ─────────────────────────────────────────────
 *
 * A name, how long it takes, what it costs, and whether it is done on a tooth.
 * Everything else the catalogue can hold — a category, a bill of materials, a
 * code of the practice's own — is left to Settings, because this is a detour
 * from finishing a visit and every extra field makes it a worse one. What is
 * created here is an ordinary catalogue row and can be completed there later.
 *
 * **The code is generated rather than asked for.** It has to be unique in the
 * branch and nobody mid-visit has a scheme in mind; a clashing code would
 * refuse the save for a reason that has nothing to do with the treatment.
 */
export function QuickProcedure({
  onCreated,
  onCancel,
}: {
  /** The new row, so the caller can select it immediately. */
  onCreated: (code: ProcedureCode) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const here = useBookingScoped();
  const [label, setLabel] = useState("");
  const [minutes, setMinutes] = useState("30");
  const [price, setPrice] = useState("");
  const [perTooth, setPerTooth] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Every procedure needs one of the clinic's categories (PG-D4), even one added here. */
  const [categories, setCategories] = useState<readonly ProcedureCategory[] | null>(null);
  const [categoryId, setCategoryId] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    here(listProcedureCategories)(controller.signal)
      .then((rows) => {
        if (!controller.signal.aborted) setCategories(rows);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setCategories([]);
        setError(cause instanceof Error ? cause.message : "Could not read the categories.");
      });
    return () => controller.abort();
  }, []);

  /**
   * **`|| 30` and `|| 0` are gone, and they were the same bug as `2e`**
   * (QA20-13).
   *
   * `Number("") || 30` is 30, and `type="number"` reported an empty string for
   * anything it could not parse — so a clinician who typed `2e` into Minutes
   * got a procedure lasting half an hour, entered into their own catalogue,
   * with nothing said. The fallback also put a **hardcoded clinical duration**
   * in a component, which *Data is configured, not coded* rules out on its own.
   *
   * Refusing the submit is the honest version: the field says what is wrong and
   * the button waits.
   */
  const minutesError = describeNumberError(minutes, MINUTES);
  const priceError = describeNumberError(price, PRICE);
  const numbersOk = minutesError === null && priceError === null && minutes.trim() !== "";

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (label.trim() === "" || categoryId === "" || !numbersOk || busy) return;

    setBusy(true);
    setError(null);

    try {
      const created = await here(createProcedureCode)({
        // Derived from the name and stamped, so two people adding "Crown" a
        // minute apart do not collide on the code.
        code: `${slugOf(label)}-${String(Date.now() % 100_000)}`,
        label: label.trim(),
        procedureCategoryId: categoryId,
        // Both guarded by `numbersOk` above, so neither needs a fallback —
        // and neither may have one, because a fallback here is a duration this
        // application chose for somebody else's catalogue.
        defaultDurationMinutes: Number(minutes),
        // **Minor units.** The box asks for whole currency and this converts;
        // sending what was typed would make every price a hundredth of itself.
        // An empty price is genuinely zero — a procedure with no price set.
        defaultPriceCents: Math.round((price.trim() === "" ? 0 : Number(price)) * 100),
        scope: perTooth ? "PER_TOOTH" : "WHOLE_MOUTH",
      });
      onCreated(created);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not add that procedure.");
      setBusy(false);
    }
  }

  return (
    <form
      className={styles.form}
      onSubmit={(event) => void submit(event)}
      data-testid="quick-procedure"
    >
      <p className={styles.lede}>
        It will be added to this location&rsquo;s catalogue, and you can fill in the rest from
        Settings later.
      </p>

      <label className={styles.field}>
        <span className={styles.label}>
          What is it called
          <RequiredMark />
        </span>
        <input
          className={styles.input}
          value={label}
          autoFocus
          disabled={busy}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Composite filling"
          data-testid="quick-procedure-label"
        />
      </label>

      <label className={styles.field}>
        <span className={styles.label}>
          Category
          <RequiredMark />
        </span>
        <select
          className={styles.input}
          value={categoryId}
          disabled={busy || categories === null}
          onChange={(event) => setCategoryId(event.target.value)}
          data-testid="quick-procedure-category"
        >
          <option value="" disabled>
            {categories === null ? "Reading…" : "Choose one…"}
          </option>
          {(categories ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {row.name}
            </option>
          ))}
        </select>
      </label>

      <div className={styles.row}>
        <NumberField
          label="Minutes"
          required
          className={styles.field}
          labelClassName={styles.label}
          inputClassName={styles.input}
          value={minutes}
          onChange={setMinutes}
          rules={MINUTES}
          disabled={busy}
          testId="quick-procedure-minutes"
        />

        {/*
          **Not `integer`** — a price has a fractional part in most currencies,
          and the minor-unit conversion happens on submit. The clinic's currency
          decides how many places there are; this only refuses what could never
          be part of any of them.
        */}
        <NumberField
          label="Price"
          required
          className={styles.field}
          labelClassName={styles.label}
          inputClassName={styles.input}
          value={price}
          onChange={setPrice}
          rules={PRICE}
          disabled={busy}
          placeholder="0"
          testId="quick-procedure-price"
        />
      </div>

      {/*
        **Per tooth by default, because most dentistry is** (TP12) — the same
        default the catalogue screen takes, so a procedure added here behaves
        like one added there.
      */}
      <label className={styles.check}>
        <input
          type="checkbox"
          checked={perTooth}
          disabled={busy}
          onChange={(event) => setPerTooth(event.target.checked)}
          data-testid="quick-procedure-per-tooth"
        />
        <span>Done on a particular tooth</span>
      </label>

      {error !== null && (
        <p className={styles.error} role="alert" data-testid="quick-procedure-error">
          {error}
        </p>
      )}

      <div className={styles.actions}>
        <button
          type="submit"
          className={styles.primary}
          disabled={busy || label.trim() === "" || categoryId === "" || !numbersOk}
          data-testid="quick-procedure-save"
        >
          {busy ? "Adding…" : "Add and use it"}
        </button>
        <button type="button" className={styles.ghost} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** `Composite filling` → `composite-filling`, trimmed to something code-shaped. */
function slugOf(label: string): string {
  return (
    label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 20) || "procedure"
  );
}
